<template>
  <div class="reports">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('teamReports.title') }}</h1>
        <p class="page-sub">{{ t('teamReports.sub') }}</p>
      </div>
      <button class="btn btn-primary" :disabled="!rows.length" @click="printPdf">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        {{ t('teamReports.print') }}
      </button>
    </div>

    <!-- Filters -->
    <div class="filters">
      <div class="f-field">
        <label>{{ t('teamReports.client') }}</label>
        <select v-model="clientId" class="form-control" @change="load()">
          <option :value="0">{{ t('teamReports.selectClient') }}</option>
          <option v-for="c in clients" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div class="f-field">
        <label>{{ t('teamReports.projectCol') }}</label>
        <select v-model="projectId" class="form-control" :disabled="!clientId" @change="load()">
          <option :value="0">{{ t('teamReports.allProjects') }}</option>
          <option v-for="p in reportProjects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
      </div>
      <div class="f-field">
        <label>{{ t('teamReports.teamMember') }}</label>
        <select v-model="userId" class="form-control" @change="load()">
          <option :value="0">{{ t('teamReports.allMembers') }}</option>
          <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
        </select>
      </div>
      <div class="f-field">
        <label>{{ t('common.from') }}</label>
        <input v-model="from" type="date" class="form-control" @change="load()" />
      </div>
      <div class="f-field">
        <label>{{ t('common.to') }}</label>
        <input v-model="to" type="date" class="form-control" @change="load()" />
      </div>
      <button v-if="from || to || projectId || userId" class="btn btn-ghost btn-sm f-reset" @click="resetFilters">{{ t('common.reset') }}</button>
    </div>

    <div v-if="!clientId" class="empty-state"><p>{{ t('teamReports.noClient') }}</p></div>
    <div v-else-if="loading" class="spinner"></div>
    <div v-else-if="!rows.length" class="empty-state"><p>{{ t('teamReports.noData') }}</p></div>

    <template v-else>
      <!-- Summary -->
      <div class="bill-strip">
        <div class="bill"><div class="bv">{{ summary.hours }}h</div><div class="bl">{{ t('teamReports.workInPeriod') }}</div></div>
        <div class="bill"><div class="bv">{{ rows.length }}</div><div class="bl">{{ t('teamReports.subject') }}</div></div>
        <div class="bill"><div class="bv">{{ summary.amount != null ? fmtMoney(summary.amount, summary.currency) : '—' }}</div><div class="bl">{{ t('teamReports.totalDue') }}</div></div>
      </div>

      <!-- Table -->
      <div class="card table-card">
        <table class="rep-table">
          <thead>
            <tr>
              <th class="num">{{ t('teamReports.ticketId') }}</th>
              <th>{{ t('teamReports.subject') }}</th>
              <th>{{ t('teamReports.projectCol') }}</th>
              <th>{{ t('export.assignee') }}</th>
              <th>{{ t('teamReports.dateCol') }}</th>
              <th class="num">{{ t('teamReports.timeCol') }}</th>
              <th class="num">{{ t('teamReports.costCol') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.task_id">
              <td class="num mono">#{{ r.task_id }}</td>
              <td>{{ r.title }}</td>
              <td class="muted">{{ r.project_name || '—' }}</td>
              <td class="muted">{{ r.assignee_name || '—' }}</td>
              <td class="dates">
                <span v-for="(d, i) in r.dates" :key="i" class="date-chip">{{ fmtDate(d.date) }}</span>
              </td>
              <td class="num">{{ r.hours }}h</td>
              <td class="num">{{ r.cost != null ? fmtMoney(r.cost, r.currency) : '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { fmtMoney } from '@/utils/billing'
import { printReport, escapeHtml } from '@/utils/printReport'

export default {
  name: 'ReportsView',
  setup() {
    const { t, locale, isRtl } = useI18n()
    const clients  = ref([])
    const users    = ref([])
    const loading  = ref(false)
    const clientId = ref(0)
    const projectId = ref(0)
    const userId   = ref(0)
    const from     = ref('')
    const to       = ref('')

    const rows    = ref([])
    const summary = ref({ hours: 0, amount: null, currency: 'ILS' })
    const reportProjects = ref([])

    function fmtDate(d) {
      if (!d) return ''
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(d).toLocaleDateString(loc, { day: '2-digit', month: '2-digit', year: '2-digit' })
    }

    function resetFilters() {
      projectId.value = 0; userId.value = 0; from.value = ''; to.value = ''
      load()
    }

    async function load() {
      if (!clientId.value) { rows.value = []; return }
      loading.value = true
      try {
        const res = await api.getReport({
          client_id: clientId.value,
          project_id: projectId.value || '',
          user_id: userId.value || '',
          from: from.value, to: to.value,
        })
        rows.value = res.data?.rows || []
        summary.value = res.data?.summary || { hours: 0, amount: null, currency: 'ILS' }
        reportProjects.value = res.data?.projects || []
      } catch { rows.value = [] } finally { loading.value = false }
    }

    function clientName() {
      return clients.value.find(c => c.id === clientId.value)?.name || ''
    }

    function printPdf() {
      const dir = isRtl.value ? 'rtl' : 'ltr'
      const head = `<tr>
        <th class="num">${escapeHtml(t('teamReports.ticketId'))}</th>
        <th>${escapeHtml(t('teamReports.subject'))}</th>
        <th>${escapeHtml(t('teamReports.projectCol'))}</th>
        <th>${escapeHtml(t('export.assignee'))}</th>
        <th>${escapeHtml(t('teamReports.dateCol'))}</th>
        <th class="num">${escapeHtml(t('teamReports.timeCol'))}</th>
        <th class="num">${escapeHtml(t('teamReports.costCol'))}</th>
      </tr>`
      const body = rows.value.map(r => `<tr>
        <td class="num">#${r.task_id}</td>
        <td>${escapeHtml(r.title)}</td>
        <td>${escapeHtml(r.project_name || '—')}</td>
        <td>${escapeHtml(r.assignee_name || '—')}</td>
        <td class="sub-dates">${r.dates.map(d => escapeHtml(fmtDate(d.date))).join(', ')}</td>
        <td class="num">${r.hours}h</td>
        <td class="num">${r.cost != null ? escapeHtml(fmtMoney(r.cost, r.currency)) : '—'}</td>
      </tr>`).join('')
      const amount = summary.value.amount != null ? fmtMoney(summary.value.amount, summary.value.currency) : '—'
      const summaryHtml = `<div class="summary">
        <div class="srow"><span>${escapeHtml(t('teamReports.workInPeriod'))}</span><span>${summary.value.hours}h</span></div>
        <div class="srow total"><span>${escapeHtml(t('teamReports.totalDue'))}</span><span>${escapeHtml(amount)}</span></div>
      </div>`
      const bodyHtml = `<table><thead>${head}</thead><tbody>${body}</tbody></table>${summaryHtml}`

      const period = (from.value || '…') + ' — ' + (to.value || '…')
      printReport({
        title: `${t('teamReports.reportTitle')} — ${clientName()}`,
        subtitle: `${t('teamReports.period')}: ${period}`,
        generated: `${t('teamReports.generated')} ${fmtDate(new Date().toISOString())}`,
        dir,
        bodyHtml,
      })
    }

    onMounted(async () => {
      try { clients.value = (await api.getClients()).data || [] } catch {}
      try { users.value = (await api.getUsers()).data || [] } catch {}
    })

    return {
      t, clients, users, loading, clientId, projectId, userId, from, to,
      rows, summary, reportProjects, fmtMoney, fmtDate, load, resetFilters, printPdf,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.muted { color: var(--muted); }
.mono { font-family: var(--font-mono); }

.filters {
  display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px;
  margin-bottom: 22px; padding: 14px 16px;
  background: var(--surface); border: 1.5px solid var(--card-brd); border-radius: var(--radius);
}
.f-field { display: flex; flex-direction: column; gap: 5px; }
.f-field label { font-size: 11px; color: var(--muted); }
.f-field .form-control { min-width: 150px; padding: 6px 10px; font-size: 13px; }
.f-reset { align-self: flex-end; }

.bill-strip { display: grid; grid-template-columns: repeat(3, 1fr); border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface); overflow: hidden; margin-bottom: 22px; }
.bill { padding: 14px 18px; }
.bill + .bill { border-inline-start: 1px solid var(--border); }
.bv { font-size: var(--fs-h2); font-weight: 600; font-variant-numeric: tabular-nums; }
.bl { font-size: 11px; color: var(--muted); margin-top: 5px; }

.card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); }
.table-card { padding: 0; overflow: auto; }
.rep-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.rep-table th { text-align: start; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: var(--muted); font-weight: 600; padding: 12px 14px; border-bottom: 1px solid var(--border); white-space: nowrap; }
.rep-table td { padding: 11px 14px; border-bottom: 1px solid var(--border); }
.rep-table tr:last-child td { border-bottom: none; }
.rep-table .num { text-align: end; font-variant-numeric: tabular-nums; white-space: nowrap; }
.dates { display: flex; flex-wrap: wrap; gap: 4px; }
.date-chip { font-family: var(--font-mono); font-size: 10.5px; color: var(--muted); background: var(--surface-2); border: 1px solid var(--border); border-radius: 5px; padding: 1px 6px; }

@media (max-width: 768px) {
  .bill-strip { grid-template-columns: 1fr; }
  .bill + .bill { border-inline-start: none; border-top: 1px solid var(--border); }
}
</style>
